import { useMutation, useQuery } from '@tanstack/react-query';
import { IUserDto, IUserId } from '@/types/user';
import { USER_KEY } from '@/api/constants/queryKeys';
import {
  IApiKey,
  ICreateApiKeyRequest,
  IEditUserPasswordRequest,
  IEditUserRequest,
  IMcpConnection,
  INewApiKey,
} from '@/api/user/user.types';
import { UserApi } from '@/api/user/user.api';
import { useApi } from '@/providers/ApiContextProvider/ApiContextProvider';
import { getIdFromToken } from '@/hooks/useUserIdFromToken';
import { handleCatch, handleCatchWithCustomError } from '@/api/helpers';
import { CustomApiError } from '@/types/error';

export const useGetUser = (userApi: UserApi) => {
  const {
    mutate: fetchingUser,
    isError,
    isSuccess,
    isPending,
  } = useMutation<IUserDto, Error, string>({
    mutationKey: [USER_KEY, 'GET'],
    mutationFn: (token: string) => {
      const userId = getIdFromToken(token);
      if (!userId) throw new Error('Received invalid token.');

      return userApi.getUser(token, userId).catch(reason => handleCatch(reason, `load user data`));
    },
  });

  return {
    isError,
    isSuccess,
    isPending,
    fetchingUser,
  };
};

export const useGetUserById = ({ id }: IUserId) => {
  const { userApi } = useApi();

  const {
    data: fetchedUser,
    isLoading,
    isSuccess,
    error,
  } = useQuery<IUserId, Error, IUserDto>({
    queryKey: [USER_KEY, 'GET', 'BY_ID', id],
    queryFn: () => {
      return userApi.getUserById(id).catch(reason => handleCatch(reason, `load user data`));
    },
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  return {
    fetchedUser,
    isLoading,
    isSuccess,
    error,
  };
};

export const useEditUser = () => {
  const { userApi } = useApi();

  const {
    isPending,
    error,
    mutate: editUser,
  } = useMutation<IUserDto, Error | CustomApiError, IEditUserRequest>({
    mutationKey: [USER_KEY, 'EDIT'],
    mutationFn: (payload: IEditUserRequest) => {
      return userApi.editUser(payload).catch(reason => {
        return handleCatchWithCustomError(reason, `edit user data`);
      });
    },
  });

  return {
    error,
    editUser,
    isLoading: isPending,
  };
};

export const useEditUserPassword = () => {
  const { userApi } = useApi();

  const {
    isPending,
    error,
    mutate: editUserPassword,
  } = useMutation<IUserDto, Error, IEditUserPasswordRequest>({
    mutationKey: [USER_KEY, 'EDIT_PASSWORD'],
    mutationFn: (payload: IEditUserPasswordRequest) => {
      return userApi.editUserPassword(payload).catch(reason => {
        return handleCatch(reason, `edit user's password`);
      });
    },
  });

  return {
    isLoading: isPending,
    error,
    editUserPassword,
  };
};

export const useCreateApiKey = () => {
  const { userApi } = useApi();

  const {
    error,
    mutate: createApiKey,
    isPending,
  } = useMutation<INewApiKey, Error, ICreateApiKeyRequest>({
    mutationKey: [USER_KEY, 'CREATE_API_KEY'],
    mutationFn: (payload: ICreateApiKeyRequest) => {
      return userApi.createApiKey(payload).catch(reason => {
        return handleCatch(reason, `create api key`);
      });
    },
  });

  return {
    error,
    createApiKey,
    isLoading: isPending,
  };
};

export const useGetApiKeys = () => {
  const { userApi } = useApi();

  const {
    data: fetchedApiKeys,
    isLoading,
    isSuccess,
    error,
  } = useQuery<IApiKey[], Error>({
    queryKey: [USER_KEY, 'GET_LIST_OF_API_KEYS'],
    queryFn: () => {
      return userApi.getApiKey().catch(reason => handleCatch(reason, `load user API keys`));
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  return {
    fetchedApiKeys,
    isLoading,
    isSuccess,
    error,
  };
};

export const useDeleteApiKey = () => {
  const { userApi } = useApi();

  const { error, mutate: deleteApiKey } = useMutation<void, Error, string>({
    mutationKey: [USER_KEY, 'DELETE_API_KEY'],
    mutationFn: (keyId: string) => {
      return userApi.deleteApiKey(keyId).catch(reason => {
        return handleCatch(reason, `delete api key`);
      });
    },
  });

  return {
    error,
    deleteApiKey,
  };
};

export const MCP_CONNECTIONS_QUERY_KEY = [USER_KEY, 'GET_MCP_CONNECTIONS'];

export const useGetMcpConnections = () => {
  const { userApi } = useApi();

  const {
    data: fetchedMcpConnections,
    isLoading,
    error,
  } = useQuery<IMcpConnection[], Error>({
    queryKey: MCP_CONNECTIONS_QUERY_KEY,
    queryFn: () => {
      return userApi
        .getMcpConnections()
        .catch(reason => handleCatch(reason, `load connected AI assistants`));
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  return {
    fetchedMcpConnections,
    isLoading,
    error,
  };
};

export const useDeleteMcpConnection = () => {
  const { userApi } = useApi();

  const { error, mutate: deleteMcpConnection } = useMutation<void, Error, string>({
    mutationKey: [USER_KEY, 'DELETE_MCP_CONNECTION'],
    mutationFn: (connectionId: string) => {
      return userApi.deleteMcpConnection(connectionId).catch(reason => {
        return handleCatch(reason, `disconnect AI assistant`);
      });
    },
  });

  return {
    error,
    deleteMcpConnection,
  };
};
